import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PageContainer,
  Tabs,
  Badge,
  Button,
  Skeleton,
  Avatar,
  ConfirmDialog,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import type { StatusKey } from '@bbpos/ui';
import { useState, useEffect, useCallback } from 'react';
import { userService } from '@/services/user.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { UserProfileTab } from '@/components/features/users/UserProfileTab';
import { UserLocationsTab } from '@/components/features/users/UserLocationsTab';
import { UserActivityTab } from '@/components/features/users/UserActivityTab';
import { UserPerformanceTab } from '@/components/features/users/UserPerformanceTab';
import { UserPosPinCard } from '@/components/features/users/UserPosPinCard';
import type { TenantRole } from '@/types/user';
import { ROLE_BADGE_VARIANT, ROLE_LEVELS } from '@/types/user';

const PANEL_CLASS =
  'rounded-2xl border border-slate-200/80 bg-white shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)]';

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

function toneClasses(tone: 'primary' | 'success' | 'warning' | 'danger' | 'info') {
  switch (tone) {
    case 'success':
      return { icon: 'bg-emerald-50 text-emerald-600', accent: 'from-emerald-500/20 to-emerald-500/0' };
    case 'warning':
      return { icon: 'bg-amber-50 text-amber-600', accent: 'from-amber-500/18 to-amber-500/0' };
    case 'danger':
      return { icon: 'bg-rose-50 text-rose-600', accent: 'from-rose-500/18 to-rose-500/0' };
    case 'info':
      return { icon: 'bg-sky-50 text-sky-600', accent: 'from-sky-500/18 to-sky-500/0' };
    default:
      return { icon: 'bg-indigo-50 text-indigo-600', accent: 'from-indigo-500/18 to-indigo-500/0' };
  }
}

function DetailStatCard({
  label,
  value,
  supporting,
  tone,
  icon,
}: {
  label: string;
  value: string | number;
  supporting?: string;
  tone: 'primary' | 'success' | 'warning' | 'danger' | 'info';
  icon: React.ReactNode;
}) {
  const palette = toneClasses(tone);
  return (
    <div className={cn(PANEL_CLASS, 'relative overflow-hidden p-5')}>
      <div className={cn('absolute inset-x-0 top-0 h-14 bg-gradient-to-b', palette.accent)} />
      <div className="relative space-y-3">
        <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl', palette.icon)}>
          {icon}
        </div>
        <div>
          <p className="text-sm font-medium text-slate-500">{label}</p>
          <p className="mt-1 text-xl font-semibold tracking-tight text-slate-950 tabular-nums">{value}</p>
        </div>
        {supporting && <p className="text-xs text-slate-400">{supporting}</p>}
      </div>
    </div>
  );
}

const TABS = [
  { id: 'profile', label: 'Profile' },
  { id: 'locations', label: 'Locations' },
  { id: 'activity', label: 'Activity' },
  { id: 'performance', label: 'Performance' },
];

function UserDetailContent() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const profile = useAuthStore((s) => s.profile);
  const businessType = profile?.tenant?.business_type ?? 'RETAIL';
  const currentRoleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;

  const activeTab = searchParams.get('tab') ?? 'profile';
  const [showDeactivate, setShowDeactivate] = useState(false);

  const { data: user, isLoading, error } = useQuery({
    queryKey: ['user', id],
    queryFn: () => userService.getById(id!),
    enabled: Boolean(id),
  });

  const { data: userLocations } = useQuery({
    queryKey: ['user', id, 'locations'],
    queryFn: () => userService.getLocations(id!),
    enabled: Boolean(id),
  });

  // Redirect on USER_NOT_FOUND per spec §3.9
  useEffect(() => {
    if (error) {
      toast({ title: 'User not found', variant: 'error' });
      navigate('/users', { replace: true });
    }
  }, [error, navigate, toast]);

  const updateMutation = useMutation({
    mutationFn: (data: { name?: string; role?: TenantRole; phone?: string; is_active?: boolean }) =>
      userService.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      toast({ title: 'User updated successfully', variant: 'success' });
    },
    onError: (err: Error) => {
      const msg = err.message ?? '';
      if (msg.includes('ROLE_ESCALATION')) {
        toast({ title: 'Cannot assign a role higher than your own', variant: 'error' });
      } else {
        toast({ title: msg || 'Failed to update user', variant: 'error' });
      }
    },
  });

  const locationMutation = useMutation({
    mutationFn: (locationIds: string[]) => userService.assignLocations(id!, locationIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      toast({ title: 'Location assignments updated', variant: 'success' });
    },
    onError: (err: Error) => toast({ title: err.message || 'Failed to update locations', variant: 'error' }),
  });

  const deactivateMutation = useMutation({
    mutationFn: () => userService.deactivate(id!),
    onSuccess: () => {
      setShowDeactivate(false);
      toast({ title: 'User deactivated and sessions invalidated', variant: 'success' });
      navigate('/users');
    },
    onError: (err: Error) => toast({ title: err.message || 'Failed to deactivate user', variant: 'error' }),
  });

  const inviteMutation = useMutation({
    mutationFn: () => userService.sendInvite(id!),
    onSuccess: () => toast({ title: 'Invite sent', variant: 'success' }),
    onError: (err: Error) => toast({ title: err.message || 'Failed to send invite', variant: 'error' }),
  });

  // Ctrl+S to save
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        const saveBtn = document.querySelector<HTMLButtonElement>('[data-save-profile]');
        saveBtn?.click();
      }
    },
    [],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (isLoading) {
    return (
      <div className="space-y-4 animate-fade-in">
        <Skeleton variant="rect" height={80} />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={120} />
          ))}
        </div>
        <Skeleton variant="rect" height={40} />
        <Skeleton variant="rect" height={300} />
      </div>
    );
  }

  if (error || !user) return null;

  const locations = userLocations ?? user.locations ?? [];
  const roleVariant: StatusKey = ROLE_BADGE_VARIANT[user.role] ?? 'draft';
  const userLevel = ROLE_LEVELS[user.role] ?? 0;
  const isSelf = user.id === profile?.id;
  const canDeactivate = user.is_active && !isSelf && userLevel < currentRoleLevel;

  return (
    <div className="animate-fade-in">
      {/* User Header Card */}
      <div className={cn(PANEL_CLASS, 'p-5 mb-6')}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Avatar name={user.name} size="lg" />
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-slate-900 truncate">
              {user.name}
            </h2>
            <p className="text-sm text-slate-500">{user.email}</p>
            {user.phone && <p className="text-xs text-slate-400 tabular-nums">{user.phone}</p>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={roleVariant}>{user.role.replace(/_/g, ' ')}</Badge>
            <Badge variant={user.is_active ? 'active' : 'draft'} dot>
              {user.is_active ? 'Active' : 'Inactive'}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => inviteMutation.mutate()}
              disabled={inviteMutation.isPending}
            >
              {inviteMutation.isPending ? 'Sending...' : 'Re-send Invite'}
            </Button>
            {canDeactivate && (
              <Button variant="danger" size="sm" onClick={() => setShowDeactivate(true)}>
                Deactivate
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-6">
        <DetailStatCard
          label="Role"
          value={user.role.replace(/_/g, ' ')}
          supporting={`Since ${new Date(user.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`}
          tone="primary"
          icon={
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
          }
        />
        <DetailStatCard
          label="Locations"
          value={locations.length}
          supporting={locations.map((l) => l.name).join(', ') || 'None assigned'}
          tone="info"
          icon={
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
            </svg>
          }
        />
        <DetailStatCard
          label="Last Active"
          value={user.last_login_at ? new Date(user.last_login_at).toLocaleDateString('en-IN') : 'Never'}
          supporting={user.last_login_at ? new Date(user.last_login_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : undefined}
          tone="warning"
          icon={
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <DetailStatCard
          label="MFA"
          value={user.mfa_enabled ? 'Enabled' : 'Not Set Up'}
          tone={user.mfa_enabled ? 'success' : 'danger'}
          icon={
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
          }
        />
      </div>

      {/* T16: only a tenant owner sets staff PINs (platform roles have no tenant) */}
      {profile?.role === 'OWNER' && <UserPosPinCard userId={user.id} userName={user.name} />}

      {/* Tabs */}
      <Tabs
        items={TABS}
        activeId={activeTab}
        onChange={(tabId) => {
          const params = new URLSearchParams(searchParams);
          params.set('tab', tabId);
          setSearchParams(params);
        }}
        scrollable
      />

      <div className="mt-6">
        {activeTab === 'profile' && (
          <UserProfileTab
            user={user}
            businessType={businessType}
            currentRoleLevel={currentRoleLevel}
            onSave={(data) => updateMutation.mutate(data)}
            saving={updateMutation.isPending}
          />
        )}
        {activeTab === 'locations' && (
          <UserLocationsTab
            currentLocations={locations}
            onSave={(ids) => locationMutation.mutate(ids)}
            saving={locationMutation.isPending}
          />
        )}
        {activeTab === 'activity' && <UserActivityTab userId={user.id} user={user} />}
        {activeTab === 'performance' && <UserPerformanceTab userId={user.id} user={user} />}
      </div>

      {/* Confirm Deactivate */}
      {showDeactivate && (
        <ConfirmDialog
          open
          title="Deactivate User"
          description={`This will immediately log out ${user.name} and prevent future access. Their data will be preserved.`}
          confirmLabel="Deactivate"
          variant="danger"
          loading={deactivateMutation.isPending}
          onConfirm={() => deactivateMutation.mutate()}
          onCancel={() => setShowDeactivate(false)}
        />
      )}
    </div>
  );
}

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: user } = useQuery({
    queryKey: ['user', id],
    queryFn: () => userService.getById(id!),
    enabled: Boolean(id),
  });

  return (
    <PageContainer
      title={user?.name ?? 'User Details'}
      breadcrumbs={[{ label: 'Users', href: '/users' }, { label: user?.name ?? 'Details' }]}
    >
      <ErrorBoundary>
        <UserDetailContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
